import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Stage, Layer, Rect, Line, Group, Image as KonvaImage, Circle, Text, Arrow } from 'react-konva';
import stoveSvg from './svgs/stove.svg';
import pipeStraightSvg from './svgs/pipe.svg';
import elbow90Svg from './svgs/90_opt.svg';
import elbow45Svg from './svgs/45_opt.svg';
import teeSvg from './svgs/tee.svg';
import chimneyTopStraightSvg from './svgs/pipe.svg';
import chimneyTopTeeSvg from './svgs/tee.svg';
import chimneyTop30Svg from './svgs/30_opt.svg';
import masonStraightSvg from './svgs/mason_straight_opt.svg';
import masonTeeSvg from './svgs/mason_tee_opt.svg';
import capSvg from './svgs/cap_opt.svg';

function useImg(src) {
  const [img, setImg] = useState(null);

  useEffect(() => {
    if (!src) return;
    const i = new window.Image();
    i.onload = () => setImg(i);
    i.src = src;
  }, [src]);

  return img;
}

// Renders an image tinted with a solid color by overlaying a rect with
// globalCompositeOperation="source-in" inside a cached group, so the tint
// only affects the image's opaque pixels.
function TintedImage({ image, x, y, width, height, tint, opacity = 0.9 }) {
  const groupRef = useRef(null);

  useEffect(() => {
    const node = groupRef.current;
    if (!node || !image) return;
    node.cache();
    node.getLayer()?.batchDraw();
  }, [image, x, y, width, height, tint, opacity]);

  if (!image) return null;

  return (
    <Group ref={groupRef} listening={false}>
      <KonvaImage image={image} x={x} y={y} width={width} height={height} opacity={opacity} />
      <Rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={tint}
        globalCompositeOperation="source-in"
      />
    </Group>
  );
}

export const UNIT = 60;
export const STAGE_W = 820;
export const STAGE_H = 510;
export const STOVE_W = UNIT * 1.68;   // 70% of the original UNIT * 2.4
export const STOVE_H = UNIT * 1.68;
export const STOVE_X = STAGE_W / 2 - STOVE_W / 2 - UNIT;

// Visual length of a "1 ft" straight pipe on canvas (the X1 case).
// The bonding-box height of a straight pipe scales linearly with the
// integer-floored user-input length, with a floor of 1:
//   length in [0, 1.99)  -> X1
//   length in [2, 2.99)  -> X2
//   length in [3, 3.99)  -> X3
// (so a 3 ft pipe renders 3x as tall as the X1 case, etc.)
export const STRAIGHT_PIPE_BASE_LEN = UNIT * 0.7;

const WALL_W = 24;
const WALL_X = STOVE_X + STOVE_W + UNIT * 1.5;
const FLOOR_H = 24;
const FLOOR_Y = STAGE_H - FLOOR_H;

export const STOVE_Y = FLOOR_Y - STOVE_H;
const ROOF_H = 28;                                       // slab thickness px
const ROOF_Y_OFFSET = 30;                               // roof shifted down for control clearance
const ROOF_LEFT_X = 0;                                  // fixed left end of roof aligned to left edge of canvas
export const ROOF_OUTDOOR_DEFAULT = UNIT * 1.5;                // default right overhang
export const WALL_TOP_DEFAULT = UNIT * 3;                     // starting wall top Y

// Slope angle in radians given wall top Y position
// The roof top-left is at (ROOF_LEFT_X, ROOF_Y_OFFSET); its bottom-edge must hit (WALL_X, wallTopY).
// slope = atan((wallTopY - ROOF_H - ROOF_Y_OFFSET) / (WALL_X - ROOF_LEFT_X))
function roofSlopeFromWallTop(wTopY) {
  return Math.atan((wTopY - ROOF_H - ROOF_Y_OFFSET) / (WALL_X - ROOF_LEFT_X));
}

// Y of roof TOP surface at a given canvas X for the current slope
function roofTopYAtCanvasX(canvasX, slope) {
  return Math.round(ROOF_Y_OFFSET + (canvasX - ROOF_LEFT_X) * Math.tan(slope));
}

const SNAP_PX = 40;
const END_SNAP_PX = 80;
// How close two bounding boxes must be (Manhattan edge distance, px) before
// their endpoints are even considered for a snap. Prevents distant parts from
// auto-attaching just because their endpoints happen to be in range.
const BOX_PROX_PX = 30;

// Returns the integer length multiplier for a straight pipe's canvas height.
// floor(length) clamped to a minimum of 1 — so [0, 1.99) -> 1, [2, 2.99) -> 2, etc.
function getPipeLengthMultiplier(item) {
  const length = Number(item.length ?? item.height ?? 1);
  if (!Number.isFinite(length)) return 1;
  return Math.max(1, Math.floor(length));
}

function getItemSize(item) {
  const componentType = inferComponentType(item);
  if (componentType === 'pipe-straight' || componentType === 'chimney-straight' || componentType === 'masonry-straight') {
    // Width (diameter axis) stays UNIT regardless of length. Height (length axis)
    // scales by the integer length multiplier. Rotation is applied at render time.
    return { w: UNIT, h: STRAIGHT_PIPE_BASE_LEN * getPipeLengthMultiplier(item) };
  }
  return { w: UNIT, h: UNIT };
}

function normalizeRotation(value) {
  return ((value % 360) + 360) % 360;
}

// Map a rotation angle (degrees) to one of the orientation categories used by
// the right-panel toggle and the payload assembler. Handles ALL rotation
// values (0, 30, 45, 90, 135, 150, 180, 210, 225, 270, 315, 330, ...), not
// just the four primary ones — auto-rotation can produce any of these.
function rotationToOrientation(rotation) {
  const norm = normalizeRotation(rotation);
  if (norm === 0 || norm === 180) return 'vertical';
  if (norm === 90 || norm === 270) return 'horizontal';
  if (norm === 30 || norm === 150 || norm === 210 || norm === 330) return '30';
  if (norm === 45 || norm === 135 || norm === 225 || norm === 315) return '45';
  // Off-grid angles: classify by nearest cardinal so the simulator (which
  // only consumes 'vertical' / 'horizontal' per part) gets a valid value.
  const mod180 = norm % 180;
  return (mod180 > 45 && mod180 < 135) ? 'horizontal' : 'vertical';
}

function inferComponentType(item) {
  if (item.componentType) return item.componentType;

  const lowerName = (item.name || '').toLowerCase();
  if (lowerName.includes('stove') || lowerName.includes('firestove')) return 'stove';
  if (lowerName.includes('cap')) return 'cap';
  if (lowerName.includes('chimney tee') || lowerName.includes('chimney top tee')) return 'chimney-tee';
  if (lowerName.includes('chimney straight') || lowerName.includes('chimney top straight') || lowerName === 'chimney') return 'chimney-straight';
  if (lowerName.includes('chimney 30') || lowerName.includes('chimney top 30') || lowerName.includes('30')) return 'chimney-30';
  if (lowerName.includes('masonry tee')) return 'masonry-tee';
  if (lowerName.includes('masonry straight') || lowerName.includes('masonry')) return 'masonry-straight';
  if (lowerName.includes('tee')) return 'tee';
  if (lowerName.includes('45')) return 'elbow-45';
  if (item.type === 'Elbow') return 'elbow-90';
  return 'pipe-straight';
}

function getSvgKey(item) {
  const componentType = inferComponentType(item);

  if (componentType === 'cap') return 'cap';
  if (componentType === 'tee') return 'tee';
  if (componentType === 'chimney-tee') return 'chimney-tee';
  if (componentType === 'chimney-straight') return 'chimney-straight';
  if (componentType === 'chimney-30') return 'chimney-30';
  if (componentType === 'masonry-tee') return 'masonry-tee';
  if (componentType === 'masonry-straight') return 'masonry-straight';
  if (componentType === 'elbow-45') return 'elbow-45';
  if (componentType === 'elbow-90') return 'elbow-90';
  return 'pipe-straight';
}

function getEffectiveRotation(item) {
  // Canvas renders SVGs in their natural orientation (matching sidebar icons).
  // Any extra rotation is applied via the explicit `rotation` field set by the
  // user (rotate handle) or by sidebar templates.
  return item.rotation != null ? item.rotation : 0;
}

function getRotationStep(item) {
  if (typeof item.rotationStep === 'number') return item.rotationStep;

  const componentType = inferComponentType(item);
  if (componentType === 'chimney-30') return 30;
  if (componentType === 'elbow-45') return 45;
  // Straight pipes/chimneys rotate in 15° steps so 30° and 45° angles are reachable.
  if (componentType === 'pipe-straight' || componentType === 'chimney-straight' || componentType === 'masonry-straight') return 15;
  return 90;
}

function getSvgImageProps(item, w, h) {
  const svgKey = getSvgKey(item);

  if (svgKey === 'cap') {
    return { x: 0, y: 0, width: w, height: h };
  }

  if (svgKey === 'pipe-straight' || svgKey === 'chimney-straight' || svgKey === 'masonry-straight') {
    // Straight SVG fills the local bonding box; length growth is carried by box width.
    // Bleed 1px on every side so vertically stacked segments overlap slightly. This
    // hides the sub-pixel seams ("gaps") that appear between pieces when the canvas is
    // drawn at a fractional scale, which Safari/WebKit renders differently from Chrome.
    // Render-only: snapping/port math uses the logical cell size (w, h), so alignment
    // is unaffected. Mirrors the 1px bleed already used by the fallback below.
    return {
      x: -1,
      y: -1,
      width: w + 2,
      height: h + 2,
    };
  }

  if (svgKey === 'masonry-tee') {
    // Masonry tee artwork fills the full unit cell.
    return { x: 0, y: 0, width: w, height: h };
  }

  if (svgKey === 'elbow-90') {
    // 90_opt.svg has inner whitespace; scale and shift into the bonding box.
    return {
      x: +0.10 * w,
      y: +0.18 * h,
      width: w * 1,
      height: h * 1,
    };
  }

  if (svgKey === 'elbow-45')
     // Path's input-stub bottom-center is at viewBox (6.688, 15.29) in a
     // (0, -0.5, 17, 17) viewBox. These offsets land that point at the cell's
     // bottom-center so it lines up with a vertical pipe's top-center after snap.
     return {
      x: +0.107 * w,
      y: +0.071 * h,
      width: w * 1,
      height: h * 1,
    };
  if (svgKey === 'chimney-30') {
    // Path's input-stub bottom-center is at viewBox (6.435, 14.941). Same
    // alignment goal as elbow-45.
    return {
      x: +0.121 * w,
      y: +0.092 * h,
      width: w * 1,
      height: h * 1,
    };
  }

  return { x: -1, y: -1, width: w + 2, height: h + 2 };
}

// Bounding-box polygon vertices per component, in unit space relative to the
// item's CENTER, BEFORE rotation. Default = 1x1 square. Angled elbows use a
// right triangle with the right-angle at bottom-right and the hypotenuse running
// from bottom-left to top-right (the 45-deg pipe path).
const DEFAULT_BBOX_SQUARE = [
  { x: -0.5, y: -0.5 },
  { x:  0.5, y: -0.5 },
  { x:  0.5, y:  0.5 },
  { x: -0.5, y:  0.5 },
];
// Triangle bbox for 45°/30° elbows. Right angle at bottom-left, bottom edge a
// full UNIT wide (x = -0.5 → 0.5) so a square pipe docking center-to-center on
// the input port aligns flush along the elbow's bottom. Hypotenuse runs from
// bottom-right to top-left and is shallower than the nominal 45°/30° (~43°/42°
// in canvas coords) — this is the tradeoff for keeping the bottom full-width
// inside a 1×1 unit cell. ENDPOINTS_BY_TYPE places the output port at the
// midpoint of this hypotenuse with outward dir perpendicular to it.
// Right-triangle bboxes filling the full unit cell (right-angle at bottom-left,
// hypotenuse from top-left corner to bottom-right corner). The 45° hypotenuse
// passes through the cell center at (0,0), so a unit-square pipe attached at
// the angled output (top edge or right edge of cell) shares only a boundary
// point with the triangle — SAT treats that as non-overlap, no need for a
// shrunken triangle.
const BBOX_SHAPES_BY_TYPE = {
  'elbow-45':   [{ x: -0.5, y:  0.5 }, { x:  0.5, y:  0.5 }, { x: -0.5, y: -0.5 }],
  'chimney-30': [{ x: -0.5, y:  0.5 }, { x:  0.5, y:  0.5 }, { x: -0.5, y: -0.5 }],
};

function getBboxShapeForType(componentType) {
  return BBOX_SHAPES_BY_TYPE[componentType] || DEFAULT_BBOX_SQUARE;
}

// Opening coordinates per component, in unit space relative to the item's
// CENTER. Each opening is represented by the 2 corner points of its visible
// pipe-edge (as drawn in the SVG, after imgProps offsets are applied):
//   pipe-straight: 2 ports × 2 corners = 4
//   elbow-90:      2 ports × 2 corners = 4
//   elbow-45/30:   2 ports × 2 corners = 4
//   tee/chimney-tee: 3 ports × 2 corners = 6
//   cap:            1 port  × 2 corners = 2
// y grows DOWN (canvas coords).
// Each entry's `dir` is that corner's port outward direction (matches the
// corresponding edge in EDGES_BY_TYPE). It's used by computeAttachRotation
// when a snap routes through this corner — the auto-rotation aligns this
// dir with the OPPOSITE of the snap target's dir.
const SQRT1_2 = Math.SQRT1_2;          // ≈ 0.7071  (cos 45° = sin 45°)
const COS_30 = Math.cos(Math.PI / 6);  // ≈ 0.8660
const ENDPOINTS_BY_TYPE = {
  // pipe.svg: vertical bar at viewBox x [173.67, 331.047] of [0, 503.607],
  // full height. Bar width = 31.3% of cell, centered.
  'pipe-straight': [
    { x: -0.155, y: -0.500, dir: { x: 0, y: -1 } },   // top-left of bar (top edge)
    { x:  0.157, y: -0.500, dir: { x: 0, y: -1 } },   // top-right of bar (top edge)
    { x:  0.157, y:  0.500, dir: { x: 0, y:  1 } },   // bottom-right of bar (bottom edge)
    { x: -0.155, y:  0.500, dir: { x: 0, y:  1 } },   // bottom-left of bar (bottom edge)
  ],
  'chimney-straight': [
    { x: -0.155, y: -0.500, dir: { x: 0, y: -1 } },
    { x:  0.157, y: -0.500, dir: { x: 0, y: -1 } },
    { x:  0.157, y:  0.500, dir: { x: 0, y:  1 } },
    { x: -0.155, y:  0.500, dir: { x: 0, y:  1 } },
  ],
  // Masonry straight reuses the straight-pipe port geometry (top/bottom edges).
  'masonry-straight': [
    { x: -0.155, y: -0.500, dir: { x: 0, y: -1 } },
    { x:  0.157, y: -0.500, dir: { x: 0, y: -1 } },
    { x:  0.157, y:  0.500, dir: { x: 0, y:  1 } },
    { x: -0.155, y:  0.500, dir: { x: 0, y:  1 } },
  ],
  // 90_opt.svg with imgProps (0.10, 0.18). The L-shape's two arms extend from
  // the cell's bottom and right edges inward. Input port is the bottom edge
  // of the bottom arm; output port is the right edge of the right arm.
  'elbow-90': [
    { x: -0.152, y:  0.472, dir: { x: 0, y:  1 } },   // bottom port: left corner
    { x:  0.160, y:  0.472, dir: { x: 0, y:  1 } },   // bottom port: right corner
    { x:  0.473, y: -0.153, dir: { x: 1, y:  0 } },   // right port: top corner
    { x:  0.473, y:  0.160, dir: { x: 1, y:  0 } },   // right port: bottom corner
  ],
  // 45_opt.svg with imgProps (0.107, 0.071). Bottom-edge port from path
  // vertices (4.022, 15.29)→(9.354, 15.29) and angled-edge port from
  // (6.732, 8.736)→(10.502, 12.504).
  'elbow-45': [
    { x: -0.157, y:  0.500, dir: { x: 0, y: 1 } },                       // bottom port: left
    { x:  0.158, y:  0.500, dir: { x: 0, y: 1 } },                       // bottom port: right
    { x:  0.003, y:  0.114, dir: { x: SQRT1_2, y: -SQRT1_2 } },          // angled port: top
    { x:  0.225, y:  0.336, dir: { x: SQRT1_2, y: -SQRT1_2 } },          // angled port: bottom
  ],
  // 30_opt.svg with imgProps (0.121, 0.092). Bottom-edge port from
  // (3.77, 14.941)→(9.1, 14.941) and angled-edge port from
  // (5.693, 7.78)→(10.299, 10.44).
  'chimney-30': [
    { x: -0.157, y:  0.500, dir: { x: 0, y: 1 } },                       // bottom port: left
    { x:  0.156, y:  0.500, dir: { x: 0, y: 1 } },                       // bottom port: right
    { x: -0.044, y:  0.079, dir: { x: 0.5, y: -COS_30 } },               // angled port: top
    { x:  0.227, y:  0.236, dir: { x: 0.5, y: -COS_30 } },               // angled port: bottom
  ],
  // tee.svg: black vertical column (full height) + black horizontal strip at
  // bottom + grey rect extending past cell left (the 3rd arm). 3 ports at
  // LEFT (grey rect's left edge), TOP (vertical column's top), and BOTTOM
  // (horizontal strip's bottom) — 2 corners each.
  'tee': [
    { x: -0.517, y: -0.161, dir: { x: -1, y:  0 } },   // left port: top corner
    { x: -0.517, y:  0.162, dir: { x: -1, y:  0 } },   // left port: bottom corner
    { x: -0.160, y: -0.517, dir: { x:  0, y: -1 } },   // top port: top-left
    { x:  0.163, y: -0.517, dir: { x:  0, y: -1 } },   // top port: top-right
    { x: -0.194, y:  0.517, dir: { x:  0, y:  1 } },   // bottom port: bottom-left
    { x:  0.194, y:  0.517, dir: { x:  0, y:  1 } },   // bottom port: bottom-right
  ],
  'chimney-tee': [
    { x: -0.517, y: -0.161, dir: { x: -1, y:  0 } },
    { x: -0.517, y:  0.162, dir: { x: -1, y:  0 } },
    { x: -0.160, y: -0.517, dir: { x:  0, y: -1 } },
    { x:  0.163, y: -0.517, dir: { x:  0, y: -1 } },
    { x: -0.194, y:  0.517, dir: { x:  0, y:  1 } },
    { x:  0.194, y:  0.517, dir: { x:  0, y:  1 } },
  ],
  // Masonry tee reuses the tee port geometry (left branch / top / bottom).
  'masonry-tee': [
    { x: -0.517, y: -0.161, dir: { x: -1, y:  0 } },
    { x: -0.517, y:  0.162, dir: { x: -1, y:  0 } },
    { x: -0.160, y: -0.517, dir: { x:  0, y: -1 } },
    { x:  0.163, y: -0.517, dir: { x:  0, y: -1 } },
    { x: -0.194, y:  0.517, dir: { x:  0, y:  1 } },
    { x:  0.194, y:  0.517, dir: { x:  0, y:  1 } },
  ],
  // cap_opt.svg has a small input stub at the bottom (path vertices at
  // y=16.489 in viewBox, x range [5.845, 11.155]).
  'cap': [
    { x: -0.156, y:  0.499, dir: { x: 0, y: 1 } },   // stub: left corner
    { x:  0.156, y:  0.499, dir: { x: 0, y: 1 } },   // stub: right corner
  ],
};

export function getItemWorldEndpoints(item) {
  const componentType = item.componentType || inferComponentType(item);
  const ep = ENDPOINTS_BY_TYPE[componentType] || [];
  const { w, h } = getItemSize(item);
  const cx = (item.x ?? 0) + w / 2;
  const cy = (item.y ?? 0) + h / 2;
  const rad = ((item.rotation ?? 0) * Math.PI) / 180;
  const cR = Math.cos(rad), sR = Math.sin(rad);
  // Endpoint coords in ENDPOINTS_BY_TYPE are unit-cell-relative (±0.5). Scale
  // by w / h (separately on each axis) BEFORE rotation, so a stretched bbox
  // produces correctly stretched world-space endpoints.
  return ep.map((p, i) => {
    const px = p.x * w;
    const py = p.y * h;
    return {
      x: cx + (px * cR - py * sR),
      y: cy + (px * sR + py * cR),
      // Outward unit vector at this port (rotated into world frame).
      dir: p.dir
        ? { x: p.dir.x * cR - p.dir.y * sR, y: p.dir.x * sR + p.dir.y * cR }
        : null,
      // Index into ENDPOINTS_BY_TYPE for this component — used by auto-rotate.
      intrinsicIdx: i,
    };
  });
}

// Port edges per component. Each edge connects two corner endpoints (by index
// into ENDPOINTS_BY_TYPE) and has an outward unit-vector `dir`. The diagonal
// edges of the 45° / 30° elbows are deliberately NOT listed here — they're
// handled with their own logic. Auto-snap matches edge-to-edge (anti-parallel
// dirs, midpoints close) so both corner dots couple at the joint.
const EDGES_BY_TYPE = {
  // pipe-straight endpoints: 0=top-L, 1=top-R, 2=bot-R, 3=bot-L
  'pipe-straight': [
    { c1: 0, c2: 1, dir: { x: 0, y: -1 } },   // top edge
    { c1: 3, c2: 2, dir: { x: 0, y:  1 } },   // bottom edge
  ],
  'chimney-straight': [
    { c1: 0, c2: 1, dir: { x: 0, y: -1 } },
    { c1: 3, c2: 2, dir: { x: 0, y:  1 } },
  ],
  'masonry-straight': [
    { c1: 0, c2: 1, dir: { x: 0, y: -1 } },
    { c1: 3, c2: 2, dir: { x: 0, y:  1 } },
  ],
  // elbow-90 endpoints: 0=bot-L, 1=bot-R, 2=right-T, 3=right-B
  'elbow-90': [
    { c1: 0, c2: 1, dir: { x: 0, y:  1 } },   // bottom edge
    { c1: 2, c2: 3, dir: { x: 1, y:  0 } },   // right edge
  ],
  // elbow-45 endpoints: 0=bot-L, 1=bot-R, 2=angled-T, 3=angled-B
  // Both ports listed so edge-to-edge match can connect this elbow (in ANY
  // rotation) to other parts when the dirs end up anti-parallel — e.g. a
  // horizontal pipe + 45° elbow rotated 225° (whose angled port now points
  // straight left and lines up with the pipe's right edge).
  'elbow-45': [
    { c1: 0, c2: 1, dir: { x: 0, y:  1 } },
    { c1: 2, c2: 3, dir: { x: SQRT1_2, y: -SQRT1_2 } },
  ],
  'chimney-30': [
    { c1: 0, c2: 1, dir: { x: 0, y:  1 } },
    { c1: 2, c2: 3, dir: { x: 0.5, y: -COS_30 } },
  ],
  // tee endpoints: 0=left-T, 1=left-B, 2=top-L, 3=top-R, 4=bot-L, 5=bot-R
  'tee': [
    { c1: 0, c2: 1, dir: { x: -1, y:  0 } },   // left edge
    { c1: 2, c2: 3, dir: { x:  0, y: -1 } },   // top edge
    { c1: 4, c2: 5, dir: { x:  0, y:  1 } },   // bottom edge
  ],
  'chimney-tee': [
    { c1: 0, c2: 1, dir: { x: -1, y:  0 } },
    { c1: 2, c2: 3, dir: { x:  0, y: -1 } },
    { c1: 4, c2: 5, dir: { x:  0, y:  1 } },
  ],
  'masonry-tee': [
    { c1: 0, c2: 1, dir: { x: -1, y:  0 } },
    { c1: 2, c2: 3, dir: { x:  0, y: -1 } },
    { c1: 4, c2: 5, dir: { x:  0, y:  1 } },
  ],
  // cap endpoints: 0=stub-L, 1=stub-R
  'cap': [
    { c1: 0, c2: 1, dir: { x: 0, y:  1 } },
  ],
};

// World-space port edges for an item: { p1, p2, mid, dir } in world coords,
// rotated by the item's rotation.
function getItemWorldEdges(item) {
  const componentType = item.componentType || inferComponentType(item);
  const edges = EDGES_BY_TYPE[componentType] || [];
  const eps = getItemWorldEndpoints(item);
  const rad = ((item.rotation ?? 0) * Math.PI) / 180;
  const cR = Math.cos(rad), sR = Math.sin(rad);
  return edges.map(e => {
    const p1 = eps[e.c1];
    const p2 = eps[e.c2];
    return {
      p1: { x: p1.x, y: p1.y },
      p2: { x: p2.x, y: p2.y },
      mid: { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 },
      dir: { x: e.dir.x * cR - e.dir.y * sR, y: e.dir.x * sR + e.dir.y * cR },
    };
  });
}

// Diagonal-edge corner anchors per component. Each anchor stores the
// ENDPOINTS_BY_TYPE index for its corner — the corner's position AND its
// outward direction (`dir`) are pulled from there in getItemWorldDiagAnchors.
// Carrying intrinsicIdx + dir on each anchor lets the snap path emit a
// `fixedEp` (with dir) for auto-rotation when a cardinal pipe attaches to
// the angled output.
const DIAG_ANCHORS_BY_TYPE = {
  'elbow-45':   [{ intrinsicIdx: 2 }, { intrinsicIdx: 3 }],
  'chimney-30': [{ intrinsicIdx: 2 }, { intrinsicIdx: 3 }],
};

// World-space diagonal anchors for an item, rotated by the item's rotation.
// Each anchor carries its world `dir` and `intrinsicIdx` so the snap path can
// trigger auto-rotation when a pipe corner snaps to it.
function getItemWorldDiagAnchors(item) {
  const componentType = item.componentType || inferComponentType(item);
  const anchors = DIAG_ANCHORS_BY_TYPE[componentType] || [];
  const eps = ENDPOINTS_BY_TYPE[componentType] || [];
  const { w, h } = getItemSize(item);
  const cx = (item.x ?? 0) + w / 2;
  const cy = (item.y ?? 0) + h / 2;
  const rad = ((item.rotation ?? 0) * Math.PI) / 180;
  const cR = Math.cos(rad), sR = Math.sin(rad);
  return anchors.map(a => {
    const ep = eps[a.intrinsicIdx];
    if (!ep) return null;
    const px = ep.x * w;
    const py = ep.y * h;
    return {
      x: cx + (px * cR - py * sR),
      y: cy + (px * sR + py * cR),
      dir: ep.dir
        ? { x: ep.dir.x * cR - ep.dir.y * sR, y: ep.dir.x * sR + ep.dir.y * cR }
        : null,
      intrinsicIdx: a.intrinsicIdx,
    };
  }).filter(Boolean);
}

// The stove SVG has a transparent border around its visible body. Snap
// targets are pulled inward by these amounts so a snapping pipe's edge or
// port lands on the visible body, not in the surrounding whitespace.
// The TOP has a larger inset (≈15 px) because the SVG's top transparent
// margin is bigger (the flue stub above the body). The SIDES have a small
// inset (≈2 px) — port-midpoint snap already lands the part's visual edge
// at the snap target, so a big side inset pushes the part visibly into
// the stove body. Tune if the part looks too far inside / outside.
const STOVE_BORDER_INSET = 2;                                // sides
const STOVE_TOP_INSET = STRAIGHT_PIPE_BASE_LEN / 4 + 5;      // ≈15 px (top)

// Anchor points on the stove for the auto-numbering BFS (App.js only). These
// are used to decide which item is closest to the stove; not for snapping.
export function getStoveEndpoints(sx, sy, sw, sh) {
  return [
    { x: sx + sw / 2, y: sy },
    { x: sx,          y: sy + sh / 2 },
    { x: sx + sw,     y: sy + sh / 2 },
  ];
}

// Stove snap targets are axis-locked EDGES, not points: when a pipe snaps to
// the top, only Y aligns (pipe is free to slide along X). When it snaps to a
// side, only X aligns (free to slide along Y). Each entry:
//   { axis: 'y' | 'x', value, range: [lo, hi] }
// 'axis' = which axis the snap LOCKS to `value`. The other axis must fall
// within `range` for the snap to count.
export function getStoveSnapTargets(sx, sy, sw, sh) {
  return [
    // Top edge: locks Y, X must be within the stove's horizontal span.
    { axis: 'y', value: sy + STOVE_TOP_INSET,         range: [sx, sx + sw] },
    // Left edge: locks X, Y must be within the stove's vertical span.
    { axis: 'x', value: sx + STOVE_BORDER_INSET,      range: [sy, sy + sh] },
    // Right edge: locks X.
    { axis: 'x', value: sx + sw - STOVE_BORDER_INSET, range: [sy, sy + sh] },
  ];
}

// Manhattan edge-to-edge distance between two AABBs. 0 if they touch or overlap.
function bboxManhattan(a, b) {
  const gapX = Math.max(0, Math.max(a.x, b.x) - Math.min(a.x + a.w, b.x + b.w));
  const gapY = Math.max(0, Math.max(a.y, b.y) - Math.min(a.y + a.h, b.y + b.h));
  return gapX + gapY;
}

// Convex-polygon overlap via Separating Axis Theorem. Touching edges (no
// interior intersection) count as NOT overlapping so two parts can dock flush.
function _projectOnAxis(poly, ax) {
  let min = Infinity, max = -Infinity;
  for (const v of poly) {
    const p = v.x * ax.x + v.y * ax.y;
    if (p < min) min = p;
    if (p > max) max = p;
  }
  return [min, max];
}
function polysOverlap(p1, p2) {
  // Tolerate sub-pixel overlap (in pixel units) so the SAT check doesn't
  // reject snaps that have a tiny offset from asymmetric SVG geometry —
  // e.g. an elbow's port midpoint landing 0.2 px off-center causing the
  // snapped pipe's bbox to barely clip a neighboring item's bbox.
  const EPS = 2;
  for (const poly of [p1, p2]) {
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      const len = Math.hypot(b.y - a.y, b.x - a.x);
      if (len < 1e-9) continue;
      const ax = { x: -(b.y - a.y) / len, y: (b.x - a.x) / len };
      const [min1, max1] = _projectOnAxis(p1, ax);
      const [min2, max2] = _projectOnAxis(p2, ax);
      if (max1 <= min2 + EPS || max2 <= min1 + EPS) return false;
    }
  }
  return true;
}

// Axis-aligned bounding box of a polygon (used for the proximity gate).
function polyAABB(poly) {
  let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
  for (const v of poly) {
    if (v.x < xMin) xMin = v.x;
    if (v.x > xMax) xMax = v.x;
    if (v.y < yMin) yMin = v.y;
    if (v.y > yMax) yMax = v.y;
  }
  return { x: xMin, y: yMin, w: xMax - xMin, h: yMax - yMin };
}

// World-space polygon for an item, factoring its rotation. Triangle for angled
// elbows, square for everything else (default).
export function getItemWorldPoly(item) {
  const ct = item.componentType || inferComponentType(item);
  const shape = getBboxShapeForType(ct);
  const { w, h } = getItemSize(item);
  const cx = (item.x ?? 0) + w / 2;
  const cy = (item.y ?? 0) + h / 2;
  const r = ((item.rotation ?? 0) * Math.PI) / 180;
  const cR = Math.cos(r), sR = Math.sin(r);
  return shape.map(p => {
    const px = p.x * w;
    const py = p.y * h;
    return {
      x: cx + (px * cR - py * sR),
      y: cy + (px * sR + py * cR),
    };
  });
}

// Edge-based snap with bounding-box proximity gating and overlap rejection.
// Matches port edges between parts: anti-parallel outward dirs, midpoints
// close enough. Translation aligns the edge midpoints, so both corner dots
// of the matched edges couple at the joint.
// fixedList entries can carry either:
//   - { kind: 'item', bbox, edges, endpoints, poly } — port-edge snap
//   - { kind: 'axis', axis, value, range }           — 1D edge snap (stove)
function isNonCardinalDir(d) {
  if (!d) return false;
  const ax = Math.abs(d.x), ay = Math.abs(d.y);
  // Cardinal = aligned with ±x or ±y axis (within float tolerance).
  return !((ax < 0.001 && Math.abs(ay - 1) < 0.001) || (ay < 0.001 && Math.abs(ax - 1) < 0.001));
}

function computeSnap(moving, fixedList, snapPx = SNAP_PX, boxProxPx = Math.max(BOX_PROX_PX, snapPx)) {
  let best = null;
  let bestD = snapPx;

  for (const f of fixedList) {
    if (f.kind === 'axis') {
      // Stove edge snap: lock the moving item's nearest PORT MIDPOINT to the
      // stove edge, and free-slide along the stove edge's span. Using port
      // midpoints (instead of bbox edges) handles parts whose visible shape
      // doesn't fill the bbox — e.g., a tee's side-branch port that extends
      // past the bbox while the central column is well inside the cell.
      const lockAxis = f.axis;
      const bbox = moving.bbox;
      const cxBox = bbox.x + bbox.w / 2;
      const cyBox = bbox.y + bbox.h / 2;
      const cFree = lockAxis === 'y' ? cxBox : cyBox;
      // The free-axis center check uses the bbox center so the snap fires
      // anywhere the part is over the stove edge.
      if (cFree < f.range[0] || cFree > f.range[1]) continue;
      // Pick the port whose lock-axis position is closest to f.value (the
      // stove edge). For a vertical pipe near the stove top this is the
      // bottom port; for a tee near the stove side it's the side-branch
      // port that extends past the bbox.
      const ports = moving.portMidpoints || [];
      let bestPortVal = null;
      let bestPortDist = Infinity;
      for (const p of ports) {
        const d = Math.abs(p[lockAxis] - f.value);
        if (d < bestPortDist) {
          bestPortDist = d;
          bestPortVal = p[lockAxis];
        }
      }
      if (bestPortVal == null) {
        // Fallback to bbox edge for parts without defined ports (shouldn't happen
        // in practice — all current component types are in PORT_INDEX_GROUPS).
        const lo = lockAxis === 'y' ? bbox.y : bbox.x;
        const hi = lockAxis === 'y' ? bbox.y + bbox.h : bbox.x + bbox.w;
        bestPortVal = Math.abs(lo - f.value) < Math.abs(hi - f.value) ? lo : hi;
        bestPortDist = Math.abs(bestPortVal - f.value);
      }
      if (bestPortDist < bestD) {
        bestD = bestPortDist;
        best = lockAxis === 'y'
          ? { dx: 0, dy: f.value - bestPortVal, gx: cxBox, gy: f.value }
          : { dx: f.value - bestPortVal, dy: 0, gx: f.value, gy: cyBox };
      }
      continue;
    }

    // Edge-to-edge snap (with bbox proximity gating).
    if (bboxManhattan(moving.bbox, f.bbox) > boxProxPx) continue;
    for (const me of moving.edges || []) {
      for (const fe of f.edges || []) {
        // Dir-compatibility filter: edges only match if their outward
        // directions are anti-parallel (sum ≈ 0). Keeps a horizontal port
        // from snapping to a vertical port.
        const sx = me.dir.x + fe.dir.x;
        const sy = me.dir.y + fe.dir.y;
        if (Math.abs(sx) > 0.01 || Math.abs(sy) > 0.01) continue;
        const dx = fe.mid.x - me.mid.x;
        const dy = fe.mid.y - me.mid.y;
        const d = Math.hypot(dx, dy);
        if (d < bestD) {
          bestD = d;
          best = {
            dx, dy, gx: fe.mid.x, gy: fe.mid.y,
            target: f,
          };
        }
      }
    }

    // Corner-proximity match with port-MID snap delta. Used for cases the
    // anti-parallel edge match above CAN'T resolve:
    //   (1) cardinal pipe attaching to an angled output (45° / 30° elbow)
    //   (2) cardinal pipe attaching to an already-rotated pipe (e.g.,
    //       another 45°-aligned straight further down the chain)
    // Cardinal-cardinal pairs are intentionally excluded — edge match
    // handles those cleanly.
    //
    // The MATCH distance is corner-to-corner (lenient — fires when ANY
    // sibling corner is close), but the snap DELTA is computed from the
    // matched port's MIDPOINT (so both sibling corners line up around the
    // matched midpoint instead of drifting apart).
    const findPortMidForCorner = (portMids, intrinsicIdx) =>
      portMids?.find(pm => pm.cornerIndices?.includes(intrinsicIdx));

    const fixedCornerAnchors = (f.endpoints || []).filter(e => e.dir);
    const movingCornerAnchors = (moving.endpoints || []).filter(e => e.dir);
    for (const fa of fixedCornerAnchors) {
      const fNon = isNonCardinalDir(fa.dir);
      for (const m of movingCornerAnchors) {
        // Skip cardinal-cardinal ANTI-parallel pairs — edge match aligns those
        // mid-to-mid already and any contribution here is redundant. Cardinal-
        // cardinal NON-anti-parallel pairs DO go through this branch though,
        // because they require auto-rotation (e.g. a vertical pipe attaching
        // to the LEFT-facing cardinal port of a 45°-rotated elbow — both dirs
        // are cardinal but perpendicular, so the pipe must rotate 90°).
        const mNon = isNonCardinalDir(m.dir);
        if (!fNon && !mNon) {
          const antiParallel = Math.abs(fa.dir.x + m.dir.x) < 0.01
                            && Math.abs(fa.dir.y + m.dir.y) < 0.01;
          if (antiParallel) continue;
        }
        const cornerD = Math.hypot(fa.x - m.x, fa.y - m.y);
        if (cornerD < bestD) {
          const fPortMid = findPortMidForCorner(f.portMidpoints, fa.intrinsicIdx);
          const mPortMid = findPortMidForCorner(moving.portMidpoints, m.intrinsicIdx);
          if (fPortMid && mPortMid) {
            // Port-mid snap delta: aligns midpoints so both sibling corners line up.
            bestD = cornerD;
            best = {
              dx: fPortMid.x - mPortMid.x,
              dy: fPortMid.y - mPortMid.y,
              gx: fPortMid.x, gy: fPortMid.y, target: f,
              fixedEp: fPortMid, movingEp: mPortMid,
            };
          } else {
            // Fallback to corner-to-corner if port mid isn't available.
            bestD = cornerD;
            best = {
              dx: fa.x - m.x, dy: fa.y - m.y,
              gx: fa.x, gy: fa.y, target: f,
              fixedEp: fa, movingEp: m,
            };
          }
        }
      }
    }
  }

  if (!best) return { dx: 0, dy: 0, snapped: false, guideX: null, guideY: null };

  // Verify the snapped polygon doesn't overlap any OTHER fixed-item polygon.
  // The snap target itself is excluded — overlap there is the intended joint
  // (and post-rotation will resolve geometric overlap from a triangular bbox).
  //
  // We SKIP this check when the snap matched a directional port (fixedEp.dir
  // is set), because those snaps will trigger auto-rotation in handleDragEnd
  // and moving.poly here is at the PRE-rotation pose — overlap-checking the
  // unrotated bbox against neighboring rotated parts produces false positives
  // (e.g., a vertical pipe being added to the left of an elbow whose right
  // side already has a 45°-rotated pipe).
  const isAutoRotateSnap = best.fixedEp?.dir != null;
  if (!isAutoRotateSnap) {
    const newPoly = moving.poly.map(p => ({ x: p.x + best.dx, y: p.y + best.dy }));
    for (const f of fixedList) {
      if (f.kind === 'axis') continue;
      if (f === best.target) continue;
      if (polysOverlap(newPoly, f.poly)) {
        return { dx: 0, dy: 0, snapped: false, guideX: null, guideY: null };
      }
    }
  }
  return {
    dx: best.dx,
    dy: best.dy,
    snapped: true,
    guideX: best.gx,
    guideY: best.gy,
    movingEp: best.movingEp ?? null,
    fixedEp: best.fixedEp ?? null,
  };
}

// Endpoint indices grouped by physical PORT (one entry per port: pair of
// corner endpoint indices that bracket that port edge). Used by the
// stove-axis snap so it locks to the part's port — not its bbox edge —
// which keeps a tee's extending arm from poking into the stove body.
const PORT_INDEX_GROUPS = {
  'pipe-straight':    [[0, 1], [2, 3]],
  'chimney-straight': [[0, 1], [2, 3]],
  'masonry-straight': [[0, 1], [2, 3]],
  'elbow-90':         [[0, 1], [2, 3]],
  'elbow-45':         [[0, 1], [2, 3]],
  'chimney-30':       [[0, 1], [2, 3]],
  'tee':              [[0, 1], [2, 3], [4, 5]],
  'chimney-tee':      [[0, 1], [2, 3], [4, 5]],
  'masonry-tee':      [[0, 1], [2, 3], [4, 5]],
  'cap':              [[0, 1]],
};

function getItemWorldPortMidpoints(item) {
  const eps = getItemWorldEndpoints(item);
  const ct = item.componentType || inferComponentType(item);
  const groups = PORT_INDEX_GROUPS[ct] || [];
  return groups.map(g => ({
    x: (eps[g[0]].x + eps[g[1]].x) / 2,
    y: (eps[g[0]].y + eps[g[1]].y) / 2,
    // Carry the two corner intrinsic indices that bracket this port and the
    // port's outward dir (both corners share the same dir). Lets the corner-
    // to-corner snap path align port MIDPOINTS (so both sibling corners end
    // up paired correctly) instead of arbitrary single-corner pairings.
    cornerIndices: g,
    dir: eps[g[0]]?.dir ?? null,
    intrinsicIdx: g[0],
  }));
}

// Build a snap entry for one item (kind: 'item'). Polygon is the (possibly
// triangular) collision shape used by SAT overlap. The proximity bbox is the
// full UNIT cell — using the smaller polygon AABB here would cause the gate
// to reject valid snaps for triangular elbows, since the triangle doesn't
// reach the cell edges.
function snapEntryForItem(item) {
  const poly = getItemWorldPoly(item);
  const { w, h } = getItemSize(item);
  return {
    kind: 'item',
    poly,
    bbox: { x: item.x ?? 0, y: item.y ?? 0, w, h },
    endpoints: getItemWorldEndpoints(item),
    edges: getItemWorldEdges(item),
    diagAnchors: getItemWorldDiagAnchors(item),
    portMidpoints: getItemWorldPortMidpoints(item),
  };
}

// Compute the rotation (degrees) that the moving item must take so its endpoint
// at `intrinsicIdx` has world outward direction = -fixedDirWorld. Returns null
// if the moving componentType has no endpoints with `dir` set, or if the snap
// didn't match a directional endpoint.
// Non-cardinal target dirs (e.g. the angled output of a 45° / 30° elbow, or a
// pipe that's been rotated 45° earlier in the chain) are allowed — the pipe
// will auto-rotate to align with the upstream angled port.
function computeAttachRotation(componentType, intrinsicIdx, fixedDirWorld) {
  if (!fixedDirWorld) return null;
  const ep = ENDPOINTS_BY_TYPE[componentType];
  if (!ep || !ep[intrinsicIdx] || !ep[intrinsicIdx].dir) return null;
  const m = ep[intrinsicIdx].dir;            // intrinsic, before rotation
  const targetX = -fixedDirWorld.x;          // we want world outward = opposite
  const targetY = -fixedDirWorld.y;
  // Rotation that maps (m.x, m.y) -> (targetX, targetY): R = atan2(target) - atan2(m)
  const angleM      = Math.atan2(m.y, m.x);
  const angleTarget = Math.atan2(targetY, targetX);
  let deg = (angleTarget - angleM) * 180 / Math.PI;
  // Normalize to [0, 360).
  deg = ((deg % 360) + 360) % 360;
  return deg;
}

// Wrap each axis-locked stove target with kind: 'axis' so computeSnap can
// dispatch on it. The stove's targets come from getStoveSnapTargets above.
function snapEntriesForStove(sx, sy, sw, sh) {
  return getStoveSnapTargets(sx, sy, sw, sh).map(t => ({ kind: 'axis', ...t }));
}

const Canvas = ({
  items,
  setItems,
  selectedItemIndex,
  setSelectedItemIndex,
  showStoveMenu,
  setShowStoveMenu,
  hideTitle = false,
  readOnly = false,
  fullWidth = false,
  indoorTempF = '65',
  setIndoorTempF = () => {},
  outdoorTempF = '35',
  setOutdoorTempF = () => {},
  stoveX: controlledStoveX,
  onStoveXChange,
  onStoveYChange,
  roofOutdoorOverhang: controlledRoofOutdoorOverhang,
  onRoofOutdoorOverhangChange,
  wallTopY: controlledWallTopY,
  onWallTopYChange,
  isMobile = false,
}) => {
  const [snapGuide, setSnapGuide] = useState(null);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [localStoveX, setLocalStoveX] = useState(STOVE_X);
  const stoveX = controlledStoveX ?? localStoveX;
  const roofOutdoorOverhang = controlledRoofOutdoorOverhang ?? ROOF_OUTDOOR_DEFAULT;
  const wallTopY = controlledWallTopY ?? WALL_TOP_DEFAULT;
  const setStoveXState = (next) => {
    if (typeof onStoveXChange === 'function') onStoveXChange(next);
    else setLocalStoveX(next);
  };
  const stoveNodeRef = useRef(null);
  const nodeRefs = useRef({});
  const dragState = useRef({});
  const stageWrapRef = useRef(null);
  const [stageWidth, setStageWidth] = useState(STAGE_W);
  const [stageHeight, setStageHeight] = useState(STAGE_H);
  // Phone-only view: a user-controlled {scale, x, y} that replaces the desktop
  // fit-to-content auto-scaling. null until first initialised. Desktop never
  // sets this, so it always falls back to the fit values below.
  const [mobileView, setMobileView] = useState(null);
  const lastTouchRef = useRef(null);

  const stoveImg = useImg(stoveSvg);
  const pipeStraightImg = useImg(pipeStraightSvg);
  const elbow90Img = useImg(elbow90Svg);
  const elbow45Img = useImg(elbow45Svg);
  const teeImg = useImg(teeSvg);
  const chimneyTopStraightImg = useImg(chimneyTopStraightSvg);
  const chimneyTopTeeImg = useImg(chimneyTopTeeSvg);
  const chimneyTop30Img = useImg(chimneyTop30Svg);
  const masonStraightImg = useImg(masonStraightSvg);
  const masonTeeImg = useImg(masonTeeSvg);
  const capImg = useImg(capSvg);

  const svgImages = {
    stove: stoveImg,
    'pipe-straight': pipeStraightImg,
    'elbow-90': elbow90Img,
    'elbow-45': elbow45Img,
    tee: teeImg,
    'chimney-straight': chimneyTopStraightImg,
    'chimney-tee': chimneyTopTeeImg,
    'chimney-30': chimneyTop30Img,
    'masonry-straight': masonStraightImg,
    'masonry-tee': masonTeeImg,
    cap: capImg,
  };

  // Floor / stove live at FIXED content coordinates; it's the VIEW (the Konva
  // stage scale + position computed in the fit-to-content block below) that
  // adapts to the window. The stage is bottom-aligned, so the stove is always
  // pinned to the bottom of the visible canvas and can never slide "under" it.
  const currentFloorY = STAGE_H - FLOOR_H;
  const currentStoveY = currentFloorY - STOVE_H;

  // ── Fit-to-content view ─────────────────────────────────────────────────
  // Bounding box (content coords) of everything that must stay visible: the
  // baseline design area, the stove, the floor, and every placed part. A tall
  // part (e.g. a 20 ft pipe) grows the box upward, which shrinks stageScale so
  // the whole design still fits; shortening it grows the box back. Every part
  // scales by the same factor because Konva scales the entire layer at once.
  const FIT_MARGIN = UNIT * 0.5;
  // The editable workspace seeds the box with the whole stage so the full working
  // area stays visible. The read-only preview on the Results page must not: seeding
  // it there forces the box to STAGE_H, and since content is bottom-anchored a short
  // design ends up parked at the bottom with dead space above it.
  let bxMin, byMin, bxMax, byMax;
  if (readOnly) {
    // Include the stove and floor, which are drawn, but not the wall or roof --
    // those are no longer rendered, and counting them adds invisible dead space
    // that squeezes the design.
    bxMin = stoveX;
    bxMax = stoveX + STOVE_W;
    byMin = currentStoveY;
    byMax = currentFloorY + FLOOR_H;
  } else {
    bxMin = 0; byMin = 0; bxMax = STAGE_W; byMax = STAGE_H;
  }
  for (const it of items) {
    const ab = polyAABB(getItemWorldPoly(it));
    if (ab.x < bxMin) bxMin = ab.x;
    if (ab.y < byMin) byMin = ab.y;
    if (ab.x + ab.w > bxMax) bxMax = ab.x + ab.w;
    if (ab.y + ab.h > byMax) byMax = ab.y + ab.h;
  }
  // Margin on top/left/right for breathing room; none below the floor so the
  // stove stays flush with the bottom edge of the canvas.
  const contentMinX = bxMin - FIT_MARGIN;
  const contentMaxX = bxMax + FIT_MARGIN;
  const contentMinY = byMin - FIT_MARGIN;
  const contentMaxY = byMax;
  const viewW = Math.max(1, contentMaxX - contentMinX);
  const viewH = Math.max(1, contentMaxY - contentMinY);
  // The preview sizes itself to the design rather than to a fixed box, so a short
  // chimney does not leave dead space above it. Capped at STAGE_H so a very tall
  // design is scaled down instead of growing the panel without limit.
  const previewHeight = (readOnly && stageWidth)
    ? Math.max(1, Math.min(
        STAGE_H,
        stageHeight || STAGE_H,                    // never taller than the space given,
        Math.round(stageWidth * viewH / viewW),    // nor taller than the design needs
      ))
    : stageHeight;
  // Scale DOWN to fit; never magnify past natural size (cap at 1).
  const fitScale = Math.min(1, stageWidth / viewW, previewHeight / viewH);
  // Center horizontally; bottom-align vertically (floor pinned to canvas bottom).
  const fitOffsetX = (stageWidth - viewW * fitScale) / 2 - contentMinX * fitScale;
  const fitOffsetY = previewHeight - contentMaxY * fitScale;
  // Phone-only: a fixed, finger-panned/zoomed view replaces fit-to-content so
  // parts keep their real size and the user navigates with two fingers. Desktop
  // is untouched — it always uses the fit values.
  const useMobileView = isMobile && mobileView;
  const stageScale = useMobileView ? mobileView.scale : fitScale;
  const stageOffsetX = useMobileView ? mobileView.x : fitOffsetX;
  const stageOffsetY = useMobileView ? mobileView.y : fitOffsetY;
  // Visible content rectangle, used to paint the background + grid edge-to-edge.
  const visMinX = -stageOffsetX / stageScale;
  const visMaxX = (stageWidth - stageOffsetX) / stageScale;
  const visMinY = -stageOffsetY / stageScale;
  const visMaxY = (previewHeight - stageOffsetY) / stageScale;

  // Propagate the live stove Y up to App.js so validation / outdoor-detection
  // logic can reason about where the stove actually is (not just the static
  // STOVE_Y export, which is the layout default).
  useEffect(() => {
    if (typeof onStoveYChange === 'function') onStoveYChange(currentStoveY);
  }, [currentStoveY, onStoveYChange]);

  // Roof geometry kept for snap zones / outdoor classification (no longer rendered)
  const slope = roofSlopeFromWallTop(wallTopY);
  const roofRightX = WALL_X + WALL_W + roofOutdoorOverhang;
  const roofLen = roofRightX - ROOF_LEFT_X;
  const roofTopRight = roofTopYAtCanvasX(roofRightX, slope);
  const roofBotRight = roofTopRight + ROOF_H;
  const wallH = currentFloorY - wallTopY;

  useEffect(() => {
    const updateScale = () => {
      const wrap = stageWrapRef.current;
      if (!wrap) return;
      const availableWidth = wrap.clientWidth;
      const availableHeight = wrap.clientHeight;
      if (!availableWidth) return;
      // Only measure the container here. The actual zoom (stageScale) and the
      // stage position are derived from this size AND the current design extent
      // in the fit-to-content block above, so they react to part changes too.
      setStageWidth(availableWidth);
      setStageHeight(availableHeight || STAGE_H);
    };

    updateScale();

    const wrap = stageWrapRef.current;
    if (!wrap) return;

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => updateScale());
      resizeObserver.observe(wrap);
    }

    window.addEventListener('resize', updateScale);
    return () => {
      window.removeEventListener('resize', updateScale);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  // Phone-only: initialise a fixed 1:1 view anchored at the stove (bottom-center)
  // once we know the canvas size. After that the two-finger handlers drive it.
  useEffect(() => {
    if (!isMobile || mobileView || !stageWidth || !stageHeight) return;
    const scale = 1;
    setMobileView({
      scale,
      x: stageWidth / 2 - (stoveX + STOVE_W / 2) * scale,
      y: stageHeight - currentFloorY * scale,
    });
  }, [isMobile, mobileView, stageWidth, stageHeight, stoveX, currentFloorY]);

  // Phone-only: two-finger pan + pinch-zoom on the Stage. Single-finger gestures
  // are left to Konva (dragging a part); a one-finger touch on empty space does
  // nothing. preventDefault stops the browser's own pinch-zoom / scroll.
  const handleStageTouchMove = (e) => {
    const touches = e.evt.touches;
    if (!touches || touches.length < 2) return;
    e.evt.preventDefault();
    const [a, b] = [touches[0], touches[1]];
    const center = { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
    const dist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
    const prev = lastTouchRef.current;
    if (prev && prev.dist > 0) {
      const wrap = stageWrapRef.current;
      const rect = wrap ? wrap.getBoundingClientRect() : { left: 0, top: 0 };
      const cx = center.x - rect.left;
      const cy = center.y - rect.top;
      setMobileView((v) => {
        const base = v || { scale: 1, x: 0, y: 0 };
        const newScale = Math.max(0.25, Math.min(3, base.scale * (dist / prev.dist)));
        // Keep the content point under the gesture center fixed while zooming...
        const contentX = (cx - base.x) / base.scale;
        const contentY = (cy - base.y) / base.scale;
        let nx = cx - contentX * newScale;
        let ny = cy - contentY * newScale;
        // ...then pan by however far the two-finger center moved this frame.
        nx += center.x - prev.center.x;
        ny += center.y - prev.center.y;
        return { scale: newScale, x: nx, y: ny };
      });
    }
    lastTouchRef.current = { dist, center };
  };
  const handleStageTouchEnd = () => { lastTouchRef.current = null; };

  const computeIsOutdoor = useCallback((item) => {
    const { w, h } = getItemSize(item);
    const itemLeft = item.x || 0;
    const itemRight = itemLeft + w;
    const itemTop = item.y || 0;
    const roofYAtLeft = roofTopYAtCanvasX(itemLeft, slope);
    const roofYAtRight = roofTopYAtCanvasX(itemRight, slope);
    const wallRight = WALL_X + WALL_W;

    // If a part spans both indoor and outdoor sections, treat it as indoor.
    const spansBothSections = itemLeft < wallRight && itemRight > wallRight;
    if (spansBothSections) return false;

    return (itemLeft > wallRight) ||
      (itemTop + h < Math.min(roofYAtLeft, roofYAtRight));
  }, [slope]);

  useEffect(() => {
    setItems(prev => {
      let changed = false;
      const next = prev.map(item => {
        const isOutdoor = computeIsOutdoor(item);
        if (item.isOutdoor === isOutdoor) return item;
        changed = true;
        return { ...item, isOutdoor };
      });
      return changed ? next : prev;
    });
  }, [setItems, items, computeIsOutdoor]);

  const fixedBoxes = [
    { x: WALL_X, y: wallTopY, w: WALL_W, h: wallH },
    { x: 0, y: currentFloorY, w: STAGE_W, h: FLOOR_H },
    { x: ROOF_LEFT_X, y: ROOF_Y_OFFSET, w: roofLen, h: roofBotRight - ROOF_Y_OFFSET },
    { x: stoveX, y: currentStoveY, w: STOVE_W, h: STOVE_H },
  ];

  const getLiveBox = (item, idx) => {
    const node = nodeRefs.current[idx];
    const { w, h } = getItemSize(item);

    return {
      id: idx,
      parentIdx: idx,
      x: node ? node.x() - w / 2 : (item.x || 0),
      y: node ? node.y() - h / 2 : (item.y || 0),
      w,
      h,
    };
  };

  const handleDragStart = (idx, e) => {
    setDraggingIdx(idx);
    dragState.current = {
      dragIdx: idx,
      lastX: e.target.x(),
      lastY: e.target.y(),
      dragStartY: e.target.y(),
      snap: null,
    };
  };

  const handleDragMove = (idx, e) => {
    const { w, h } = getItemSize(items[idx]);
    // No hard clamp to the stage box: parts may sit above the stove (tall
    // stacks) or out to the sides, and the fit-to-content view rescales so
    // they stay visible. Konva reports node coords in content space already.
    const cx = e.target.x();
    const cy = e.target.y();

    dragState.current.lastX = cx;
    dragState.current.lastY = cy;

    const movingItem = { ...items[idx], x: cx - w / 2, y: cy - h / 2 };
    const movingPoly = getItemWorldPoly(movingItem);
    const moving = {
      poly: movingPoly,
      bbox: { x: movingItem.x, y: movingItem.y, w, h },
      endpoints: getItemWorldEndpoints(movingItem),
      edges: getItemWorldEdges(movingItem),
      diagAnchors: getItemWorldDiagAnchors(movingItem),
      portMidpoints: getItemWorldPortMidpoints(movingItem),
    };
    const fixedList = [
      ...snapEntriesForStove(stoveX, currentStoveY, STOVE_W, STOVE_H),
      ...items.flatMap((it, i) => i === idx ? [] : [snapEntryForItem(it)]),
    ];
    const snap = computeSnap(moving, fixedList);

    // Show snap guide while dragging — apply the snap only on release in handleDragEnd.
    dragState.current.snap = snap;
    setSnapGuide(snap.snapped ? { x: snap.guideX, y: snap.guideY } : null);
  };

  const handleDragEnd = (idx) => {
    const node = nodeRefs.current[idx];
    const targetItem = items[idx];

    // Always recompute snap on release with the stronger magnetic radius.
    let snap = null;
    if (node && targetItem) {
      const { w, h } = getItemSize(targetItem);
      const movingItem = { ...targetItem, x: node.x() - w / 2, y: node.y() - h / 2 };
      const movingPoly = getItemWorldPoly(movingItem);
      const moving = {
        poly: movingPoly,
        bbox: { x: movingItem.x, y: movingItem.y, w, h },
        endpoints: getItemWorldEndpoints(movingItem),
        edges: getItemWorldEdges(movingItem),
        diagAnchors: getItemWorldDiagAnchors(movingItem),
      portMidpoints: getItemWorldPortMidpoints(movingItem),
      };
      const fixedList = [
        ...snapEntriesForStove(stoveX, currentStoveY, STOVE_W, STOVE_H),
        ...items.flatMap((it, i) => i === idx ? [] : [snapEntryForItem(it)]),
      ];
      snap = computeSnap(moving, fixedList, END_SNAP_PX);
    }

    // Auto-rotate the moving part if it's a pipe and it just docked against
    // a directional endpoint (angled fitting, or a pipe at non-cardinal rotation).
    let autoRotation = null;
    if (snap?.snapped && targetItem && snap.fixedEp?.dir && snap.movingEp) {
      const ct = targetItem.componentType || inferComponentType(targetItem);
      if (ct === 'pipe-straight' || ct === 'chimney-straight' || ct === 'masonry-straight') {
        autoRotation = computeAttachRotation(ct, snap.movingEp.intrinsicIdx, snap.fixedEp.dir);
      }
    }

    if (snap?.snapped && node) {
      // If we're auto-rotating, the snap delta computed for the un-rotated pipe
      // would offset the (now-rotated) endpoint away from the fixed port. So
      // place the pipe's center such that its POST-rotation endpoint lands on
      // fixedEp exactly.
      if (autoRotation != null && targetItem) {
        const ct = targetItem.componentType || inferComponentType(targetItem);
        // Prefer the LOCAL port midpoint of the matched port (so both sibling
        // corners line up after the rotation). Falls back to the single-corner
        // path if the snap result is from a code path that doesn't carry
        // cornerIndices (legacy paths).
        const cornerIndices = snap.movingEp.cornerIndices;
        const eps = ENDPOINTS_BY_TYPE[ct];
        let epLocal = null;
        if (cornerIndices && cornerIndices.length === 2 && eps?.[cornerIndices[0]] && eps?.[cornerIndices[1]]) {
          const epA = eps[cornerIndices[0]];
          const epB = eps[cornerIndices[1]];
          epLocal = { x: (epA.x + epB.x) / 2, y: (epA.y + epB.y) / 2 };
        } else if (eps?.[snap.movingEp.intrinsicIdx]) {
          epLocal = eps[snap.movingEp.intrinsicIdx];
        }
        if (epLocal) {
          const rad = (autoRotation * Math.PI) / 180;
          const cR = Math.cos(rad), sR = Math.sin(rad);
          // Endpoint coords are unit-cell-relative; scale by the item's actual
          // w/h before rotating so a stretched bbox (variable-length straights)
          // lands its endpoint exactly on the snap target.
          const { w: tw, h: th } = getItemSize(targetItem);
          const epPx = epLocal.x * tw;
          const epPy = epLocal.y * th;
          const offX = epPx * cR - epPy * sR;
          const offY = epPx * sR + epPy * cR;
          node.x(snap.fixedEp.x - offX);
          node.y(snap.fixedEp.y - offY);
        } else {
          node.x(node.x() + snap.dx);
          node.y(node.y() + snap.dy);
        }
      } else {
        node.x(node.x() + snap.dx);
        node.y(node.y() + snap.dy);
      }
    }

    if (node && targetItem) {
      const { w, h } = getItemSize(targetItem);
      setItems(prev => prev.map((item, i) => {
        if (i !== idx) return item;
        // Store the raw content position (no stage-box clamp); the fit-to-
        // content view keeps whatever the user places within the canvas.
        const nx = node.x() - w / 2;
        const ny = node.y() - h / 2;
        const movedItem = { ...item, x: nx, y: ny };
        if (autoRotation != null) {
          movedItem.rotation = autoRotation;
          // Tag this rotation as snap-driven so dragging the part away later
          // can revert it (see the "un-snap" branch below).
          movedItem._snapRotated = autoRotation;
          // Also reflect this in the orientation field so the right-panel
          // dropdown stays in sync.
          movedItem.orientation = rotationToOrientation(autoRotation);
        } else if (!snap?.snapped && item._snapRotated != null && (item.rotation ?? 0) === item._snapRotated) {
          // Drag-away revert: the part was earlier auto-rotated by a snap,
          // and now no snap was found AND the user hasn't manually rotated
          // it since (item.rotation still matches the saved _snapRotated).
          // Revert to default (rotation 0, vertical) and clear the flag.
          movedItem.rotation = 0;
          movedItem._snapRotated = null;
          movedItem.orientation = 'vertical';
        }
        const isOutdoor = computeIsOutdoor(movedItem);

        if (item.ambientTempOverride) {
          return { ...movedItem, isOutdoor };
        }

        return {
          ...movedItem,
          isOutdoor,
        };
      }));
    }

    setSnapGuide(null);
    setDraggingIdx(null);
    dragState.current = {};
  };

  const handleRotate = (idx, stepOverride) => {
    const target = items[idx];
    if (!target) return;

    const ct = target.componentType || inferComponentType(target);
    const isStraightPipe = ct === 'pipe-straight' || ct === 'chimney-straight' || ct === 'masonry-straight';

    // Determine next rotation. Straight pipes have a special rule for manual
    // double-click (stepOverride == null): they SNAP to the nearest cardinal
    // direction — never to 45° or 30° via this path. Angled rotations on
    // straight pipes can still come from auto-snap (handleDragEnd's
    // auto-rotation, which sets rotation directly bypassing this function).
    let nextRotation;
    if (isStraightPipe && stepOverride == null) {
      const cur = ((target.rotation ?? 0) % 360 + 360) % 360;
      if (cur === 0 || cur === 180) nextRotation = 90;            // vertical → horizontal
      else if (cur === 90 || cur === 270) nextRotation = 0;       // horizontal → vertical
      else nextRotation = 0;                                       // angled (45°/30°/…) → vertical
    } else {
      const step = stepOverride ?? getRotationStep(target);
      nextRotation = normalizeRotation((target.rotation ?? getEffectiveRotation(target)) + step);
    }

    // For straight pipes, sync `orientation` so the right-panel toggle updates
    // and the simulation payload (which only consumes 'vertical'/'horizontal' per
    // part) reflects the new orientation. Elbows have their bend baked into
    // angle_deg by component type, so we don't touch their orientation here.
    let nextOrientation = target.orientation;
    if (isStraightPipe) {
      nextOrientation = rotationToOrientation(nextRotation);
    }

    setItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      // Manual rotation clears the snap-driven rotation flag, so a later
      // drag-away won't revert this rotation back to vertical.
      return {
        ...item,
        rotation: nextRotation,
        orientation: nextOrientation,
        _snapRotated: null,
      };
    }));

    const node = nodeRefs.current[idx];
    if (node) node.rotation(nextRotation);
  };

  const handleDropFromSidebar = (e) => {
    e.preventDefault();

    const itemData = e.dataTransfer.getData('item');
    if (!itemData) return;

    const item = JSON.parse(itemData);
    const stageCanvas = e.currentTarget.querySelector('canvas');
    const rect = stageCanvas
      ? stageCanvas.getBoundingClientRect()
      : e.currentTarget.getBoundingClientRect();

    const componentType = inferComponentType(item);
    const { w, h } = getItemSize({ ...item, componentType });
    // Convert the drop point from screen px to content coords, undoing the
    // stage's scale AND its fit-to-content position offset.
    const pointerX = (e.clientX - rect.left - stageOffsetX) / stageScale;
    const pointerY = (e.clientY - rect.top - stageOffsetY) / stageScale;
    const dropX = pointerX - w / 2;
    // Allow placing above the stove (negative y) for tall stacks; just keep the
    // part from landing below the floor.
    const dropY = Math.min(currentFloorY - h, pointerY - h / 2);

    const tentativeItem = {
      ...item,
      componentType,
      selectedDiameter: item.selectedDiameter || item.diameters?.[0] || '6',
      orientation: item.orientation || 'vertical',
      length: item.length ?? item.height ?? 1,
      height: item.height ?? item.length ?? 1,
      rotation: item.rotation != null ? item.rotation : getEffectiveRotation(item),
      rotationStep: item.rotationStep || 90,
      x: dropX,
      y: dropY,
    };

    const movingPoly = getItemWorldPoly(tentativeItem);
    const moving = {
      poly: movingPoly,
      bbox: { x: tentativeItem.x, y: tentativeItem.y, w, h },
      endpoints: getItemWorldEndpoints(tentativeItem),
      edges: getItemWorldEdges(tentativeItem),
      diagAnchors: getItemWorldDiagAnchors(tentativeItem),
      portMidpoints: getItemWorldPortMidpoints(tentativeItem),
    };
    const fixedList = [
      ...snapEntriesForStove(stoveX, currentStoveY, STOVE_W, STOVE_H),
      ...items.map(it => snapEntryForItem(it)),
    ];
    const snap = computeSnap(moving, fixedList, END_SNAP_PX);

    // Determine auto-rotation for pipes docking on directional ports.
    let autoRotation = null;
    if (snap?.snapped && snap.fixedEp?.dir && snap.movingEp) {
      const ct = tentativeItem.componentType;
      if (ct === 'pipe-straight' || ct === 'chimney-straight' || ct === 'masonry-straight') {
        autoRotation = computeAttachRotation(ct, snap.movingEp.intrinsicIdx, snap.fixedEp.dir);
      }
    }

    let snappedX = tentativeItem.x;
    let snappedY = tentativeItem.y;
    let finalRotation = tentativeItem.rotation;
    if (snap.snapped) {
      if (autoRotation != null) {
        // Position so the POST-rotation endpoint (or port midpoint) lands
        // exactly on fixedEp. Prefer the LOCAL port midpoint when available
        // (matches both sibling corners) over a single-corner intrinsicIdx.
        const eps = ENDPOINTS_BY_TYPE[tentativeItem.componentType];
        const cornerIndices = snap.movingEp.cornerIndices;
        let epLocal = null;
        if (cornerIndices && cornerIndices.length === 2 && eps?.[cornerIndices[0]] && eps?.[cornerIndices[1]]) {
          const epA = eps[cornerIndices[0]];
          const epB = eps[cornerIndices[1]];
          epLocal = { x: (epA.x + epB.x) / 2, y: (epA.y + epB.y) / 2 };
        } else if (eps?.[snap.movingEp.intrinsicIdx]) {
          epLocal = eps[snap.movingEp.intrinsicIdx];
        }
        if (epLocal) {
          const rad = (autoRotation * Math.PI) / 180;
          const cR = Math.cos(rad), sR = Math.sin(rad);
          // Endpoint coords are unit-cell-relative; scale by w/h before rotating
          // so a stretched bbox (variable-height straight pipes) lands its
          // endpoint exactly on the snap target.
          const epPx = epLocal.x * w;
          const epPy = epLocal.y * h;
          const offX = epPx * cR - epPy * sR;
          const offY = epPx * sR + epPy * cR;
          // node.x/y == center == item.x + w/2, item.y + h/2
          snappedX = (snap.fixedEp.x - offX) - w / 2;
          snappedY = (snap.fixedEp.y - offY) - h / 2;
          finalRotation = autoRotation;
        } else {
          snappedX = tentativeItem.x + snap.dx;
          snappedY = tentativeItem.y + snap.dy;
        }
      } else {
        snappedX = tentativeItem.x + snap.dx;
        snappedY = tentativeItem.y + snap.dy;
      }
    }

    const newItemWithSnap = {
      ...tentativeItem,
      x: snappedX,
      y: snappedY,
      rotation: finalRotation,
    };
    if (autoRotation != null) {
      newItemWithSnap.orientation = rotationToOrientation(autoRotation);
    }

    const isOutdoor = computeIsOutdoor(newItemWithSnap);

    const newItem = {
      ...newItemWithSnap,
      isOutdoor,
    };

    setItems(prev => [...prev, newItem]);
  };

  // Grid + background span the full VISIBLE content rectangle (visMin..visMax,
  // which already accounts for the fit-to-content scale + offset) so the canvas
  // is painted edge-to-edge with no un-painted strips around the design.
  const gridLines = [];
  for (let x = Math.floor(visMinX / UNIT) * UNIT; x <= visMaxX; x += UNIT) {
    gridLines.push(
      <Line
        key={`v${x}`}
        points={[x, visMinY, x, visMaxY]}
        stroke="#c8cdd4"
        strokeWidth={0.5}
      />,
    );
  }
  for (let y = Math.floor(visMinY / UNIT) * UNIT; y <= visMaxY; y += UNIT) {
    gridLines.push(
      <Line
        key={`h${y}`}
        points={[visMinX, y, visMaxX, y]}
        stroke="#c8cdd4"
        strokeWidth={0.5}
      />,
    );
  }

  return (
    <div
      className="canvas"
      style={fullWidth ? { width: '100%', padding: 0 } : undefined}
      onDragOver={e => e.preventDefault()}
      onDrop={handleDropFromSidebar}
    >
      {!hideTitle && <h2>Canvas</h2>}
      {!readOnly && !isMobile && (
        <div style={{ marginTop: 0, marginBottom: 8, color: 'var(--text-on-light-secondary)', fontSize: 15, fontWeight: 700, lineHeight: 1.4, flexShrink: 0, textAlign: 'left', border: '2px solid var(--action-blue)', background: '#eff6ff', borderRadius: 6, padding: '10px 14px' }}>
          <div style={{ marginBottom: 4, fontSize: 18, color: '#1d4ed8' }}>To get started:</div>
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li>Drag a part from the left onto the canvas. Double-click it to rotate, or drag the blue arrow to change its length.</li>
            <li>Click a part to see and edit its settings on the right.</li>
            <li>When your design is ready, click Finalize Design. Parts are numbered automatically; to override a number, select the part and edit Part Number on the right.</li>
          </ol>
        </div>
      )}
      <div ref={stageWrapRef} style={{ position: 'relative', width: '100%', flex: 1, minHeight: 0, touchAction: isMobile ? 'none' : undefined }}>
        <div
          style={{
            position: 'relative',
            width: stageWidth,
            height: previewHeight,
          }}
        >
        <Stage
          width={stageWidth}
          height={previewHeight}
          scaleX={stageScale}
          scaleY={stageScale}
          x={stageOffsetX}
          y={stageOffsetY}
          style={{ display: 'block' }}
          onClick={e => {
            if (e.target === e.target.getStage()) setSelectedItemIndex(null);
          }}
          onTouchMove={isMobile ? handleStageTouchMove : undefined}
          onTouchEnd={isMobile ? handleStageTouchEnd : undefined}
        >
        <Layer>{gridLines}</Layer>

        <Layer listening={false}>
          <Rect x={visMinX} y={visMinY} width={visMaxX - visMinX} height={visMaxY - visMinY} fill="#f0f2f5" />
        </Layer>

        <Layer>
          {snapGuide?.x != null && snapGuide?.y != null && (
            <Circle
              x={snapGuide.x}
              y={snapGuide.y}
              radius={6}
              stroke="#0088cc"
              strokeWidth={2}
              listening={false}
            />
          )}
        </Layer>

        <Layer>
          {/* Stove */}
          <Group
            x={stoveX}
            y={currentStoveY}
            draggable
            ref={node => { stoveNodeRef.current = node; }}
            onDragMove={(e) => {
              const nx = Math.max(0, Math.min(WALL_X - STOVE_W, e.target.x()));
              e.target.position({ x: nx, y: currentStoveY });
            }}
            onDragEnd={(e) => {
              const nx = Math.max(0, Math.min(WALL_X - STOVE_W, e.target.x()));
              e.target.position({ x: nx, y: currentStoveY });
              setStoveXState(nx);
            }}
            onClick={() => setShowStoveMenu(!showStoveMenu)}
          >
            <Rect
              x={0}
              y={0}
              width={STOVE_W}
              height={STOVE_H}
              fill="rgba(0,0,0,0.001)"
            />
            {stoveImg && (
              <KonvaImage
                image={stoveImg}
                x={0}
                y={0}
                width={STOVE_W}
                height={STOVE_H}
                opacity={0.9}
                listening={false}
              />
            )}
          </Group>


        </Layer>

        <Layer>

          {items.map((item, idx) => {
            const isSelected = selectedItemIndex === idx;
            const isDragging = draggingIdx === idx;
            const componentType = inferComponentType(item);
            const isChimneyTop = componentType.startsWith('chimney-');
            const isCap = componentType === 'cap';
            const { w, h } = getItemSize(item);
            const cx = (item.x || 0) + w / 2;
            const cy = (item.y || 0) + h / 2;
            const effectiveRotation = getEffectiveRotation(item);
            const key = getSvgKey(item);
            const img = svgImages[key];
            const imgProps = getSvgImageProps(item, w, h);

            const liveNode = isDragging ? nodeRefs.current[idx] : null;
            const liveX = liveNode ? (liveNode.x() - w / 2) : (item.x || 0);
            const liveY = liveNode ? (liveNode.y() - h / 2) : (item.y || 0);
            const isOutdoor = computeIsOutdoor({ ...item, x: liveX, y: liveY });

            return (
              <Group
                key={idx}
                draggable
                ref={node => {
                  if (node) nodeRefs.current[idx] = node;
                }}
                x={cx}
                y={cy}
                offsetX={w / 2}
                offsetY={h / 2}
                rotation={effectiveRotation}
                onDragStart={e => handleDragStart(idx, e)}
                onDragMove={e => handleDragMove(idx, e)}
                onDragEnd={() => handleDragEnd(idx)}
                onClick={e => {
                  e.cancelBubble = true;
                  setSelectedItemIndex(isSelected ? null : idx);
                }}
                onDblClick={e => {
                  e.cancelBubble = true;
                  // Pass no step override so handleRotate uses the part's
                  // intrinsic rotationStep (30° for chimney-30, 45° for
                  // elbow-45, 15° for straight pipes, 90° for everything else).
                  handleRotate(idx);
                }}
                onDblTap={e => {
                  e.cancelBubble = true;
                  handleRotate(idx);
                }}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                {(() => {
                  const strokeColor = isOutdoor
                    ? 'var(--text-dimmer)'
                    : isSelected
                      ? (isChimneyTop ? '#059669' : 'var(--action-blue)')
                      : (isChimneyTop ? '#6ee7b7' : 'var(--border-subtle)');
                  const dash = isDragging ? [6, 4] : undefined;
                  const sw = isSelected ? 2 : 1;
                  const shape = getBboxShapeForType(componentType);
                  if (shape !== DEFAULT_BBOX_SQUARE) {
                    // Triangle (or any non-rectangular) bbox — render as a polygon
                    // for the visible outline, plus an invisible full-cell rect
                    // so hover/click hit detection covers the full UNIT cell
                    // (otherwise the rotate "R" button at the top-right corner
                    // sits outside the triangle and disappears on mouse-out).
                    const points = shape.flatMap(p => [(p.x + 0.5) * w, (p.y + 0.5) * h]);
                    return (
                      <>
                        <Rect
                          x={0}
                          y={0}
                          width={w}
                          height={h}
                          fill="rgba(0,0,0,0)"
                        />
                        <Line
                          points={points}
                          closed
                          fill="rgba(0,0,0,0)"
                          stroke={strokeColor}
                          strokeWidth={sw}
                          dash={dash}
                        />
                      </>
                    );
                  }
                  return (
                    <Rect
                      x={0}
                      y={0}
                      width={w}
                      height={h}
                      fill="rgba(0,0,0,0)"
                      stroke={strokeColor}
                      strokeWidth={sw}
                      dash={dash}
                      cornerRadius={1}
                    />
                  );
                })()}


                {img && (
                  isChimneyTop ? (
                    <TintedImage
                      key={`img-${idx}-${isOutdoor}`}
                      image={img}
                      x={imgProps.x}
                      y={imgProps.y}
                      width={imgProps.width}
                      height={imgProps.height}
                      tint="#363b458d"
                      opacity={0.9}
                    />
                  ) : isCap ? (
                    <TintedImage
                      key={`img-${idx}-cap`}
                      image={img}
                      x={imgProps.x}
                      y={imgProps.y}
                      width={imgProps.width}
                      height={imgProps.height}
                      tint="#363b458d"
                      opacity={0.9}
                    />
                  ) : componentType === 'masonry-straight' ? (
                    // Tile the brick image along the length axis instead of
                    // stretching it: one base-length copy per foot, stacked from
                    // the bottom up, so longer runs read as more brick courses
                    // rather than a taller distorted image.
                    Array.from({ length: getPipeLengthMultiplier(item) }).map((_, t) => (
                      <KonvaImage
                        key={`img-${idx}-tile-${t}`}
                        image={img}
                        x={0}
                        y={t * STRAIGHT_PIPE_BASE_LEN}
                        width={w}
                        height={STRAIGHT_PIPE_BASE_LEN}
                        opacity={0.9}
                        listening={false}
                      />
                    ))
                  ) : (
                    <KonvaImage
                      key={`img-${idx}-${isOutdoor}`}
                      image={img}
                      x={imgProps.x}
                      y={imgProps.y}
                      width={imgProps.width}
                      height={imgProps.height}
                      rotation={imgProps.rotation || 0}
                      offsetX={imgProps.offsetX || 0}
                      offsetY={imgProps.offsetY || 0}
                      opacity={0.9}
                      listening={false}
                    />
                  )
                )}


                {item.angleLabel && (
                  // Anchor sits in the part's local frame, so the label moves
                  // with the rotation. The Text rotates -effectiveRotation
                  // around its own center (offset = ~half text size) so the
                  // glyphs stay upright at the rotated anchor position.
                  <Text
                    x={11}
                    y={h - 13}
                    offsetX={7}
                    offsetY={5}
                    rotation={-effectiveRotation}
                    text={item.angleLabel}
                    fill="var(--border-heavy)"
                    fontSize={10}
                    listening={false}
                  />
                )}

                {typeof item.sequenceOrder === 'number' && (
                  <Group
                    x={w / 2}
                    y={h / 2}
                    offsetX={w / 2}
                    offsetY={h / 2}
                    rotation={-effectiveRotation}
                    listening={false}
                  >
                    <Circle
                      x={10}
                      y={h - 10}
                      radius={10}
                      fill="#ffeb3b"
                      stroke="#111"
                      strokeWidth={2}
                    />
                    <Text
                      x={0}
                      y={h - 18}
                      width={20}
                      height={16}
                      text={String(item.sequenceOrder)}
                      fill="#111"
                      fontStyle="bold"
                      fontSize={12}
                      align="center"
                      verticalAlign="middle"
                      listening={false}
                    />
                  </Group>
                )}

                {/* Resize handle for straight pipes — drag along the pipe's
                    length axis from the TOP of the pipe to change the user-
                    input length in 1-ft increments. The handle sits just
                    above the local bbox top edge; under rotation it follows
                    the pipe end. On release the bottom of the pipe is held
                    fixed in canvas space (item.x/y are shifted so the pipe
                    grows AWAY from its bottom), so chained parts don't get
                    pushed out of place. */}
                {isSelected && (componentType === 'pipe-straight' || componentType === 'chimney-straight' || componentType === 'masonry-straight') && (
                  <Group
                    x={w / 2}
                    y={-10}
                    draggable
                    onMouseDown={(e) => { e.cancelBubble = true; }}
                    onDragStart={(e) => { e.cancelBubble = true; }}
                    onDragMove={(e) => {
                      e.cancelBubble = true;
                      // Lock local X to the pipe's length axis so the handle
                      // never drifts sideways. Because this Group is a child of
                      // the part's rotated Group, the visible motion follows the
                      // pipe's orientation in world space.
                      e.target.x(w / 2);
                      // Handle anchor sits 10 px above the pipe top (local y=-10).
                      // Pipe length in local units = h - (anchor.y + 10).
                      const localY = e.target.y();
                      const distFromBottom = h - (localY + 10);
                      const snappedDist = Math.max(1, Math.round(distFromBottom / STRAIGHT_PIPE_BASE_LEN)) * STRAIGHT_PIPE_BASE_LEN;
                      const snappedY = h - snappedDist - 10;
                      e.target.y(snappedY);
                    }}
                    onDragEnd={(e) => {
                      e.cancelBubble = true;
                      const localY = e.target.y();
                      const distFromBottom = h - (localY + 10);
                      const newLength = Math.max(1, Math.round(distFromBottom / STRAIGHT_PIPE_BASE_LEN));
                      const newH = newLength * STRAIGHT_PIPE_BASE_LEN;
                      // Keep the local-frame BOTTOM (local (w/2, h)) anchored
                      // to its canvas position across the resize, regardless of
                      // the part's rotation:
                      //   item.x_new = item.x_old + (h_new - h_old)/2 * sin θ
                      //   item.y_new = item.y_old + (h_old - h_new)/2 * (1 + cos θ)
                      const dh = newH - h;
                      const rad = ((item.rotation ?? 0) * Math.PI) / 180;
                      const newItemX = (item.x ?? 0) + (dh / 2) * Math.sin(rad);
                      const newItemY = (item.y ?? 0) - (dh / 2) * (1 + Math.cos(rad));
                      // Reset the handle's local anchor (react-konva won't
                      // re-apply same-valued props otherwise).
                      e.target.x(w / 2);
                      e.target.y(-10);
                      setItems(prev => prev.map((it, i) => {
                        if (i !== idx) return it;
                        return {
                          ...it,
                          length: newLength,
                          height: newLength,
                          x: newItemX,
                          y: newItemY,
                        };
                      }));
                    }}
                  >
                    {/* Circular hit area / visual background. */}
                    <Circle x={0} y={0} radius={11} fill="var(--action-blue)" stroke="#1d4ed8" strokeWidth={1.5} />
                    {/* Double-headed arrow oriented along the part's length
                        axis (local ±Y). Auto-rotates with the parent Group,
                        so on a vertical pipe it points up/down and on a
                        horizontal pipe it points left/right. */}
                    <Arrow
                      points={[0, -7, 0, 7]}
                      pointerAtBeginning
                      pointerAtEnding
                      fill="white"
                      stroke="white"
                      strokeWidth={1.5}
                      pointerLength={5}
                      pointerWidth={6}
                    />
                  </Group>
                )}

              </Group>
            );
          })}
        </Layer>
      </Stage>

      </div>
      </div>
    </div>
  );
};

export default Canvas;
